import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import history from 'routing/history';
import { fetchContracts } from 'products/nomad_insurance/state/contract';
import {
  fetchClaimByID,
  resetState
} from 'products/nomad_insurance/views/ClaimsForm/state';
import Bird from 'assets/img/birds/birds-broken-leg.png';
import WaveBottom from 'products/nomad_insurance/assets/img/claims/wave_bottom.svg';
import ClaimsBg from './components/ClaimsBg';
import Stepper from './components/Stepper';
import Routes from './routes';
import { NON_MEDICAL_FLOW, MEDICAL_FLOW } from './flows';
import {
  Container,
  LoadingOverlay,
  InfoCol,
  HeadingBg,
  InfoContent,
  FormHeading,
  ChatLinkDesktop,
  Content,
  Image,
  Footer,
  ChatLinkMobile,
  IntercomLink
} from './styled';

const ClaimsForm = () => {
  const dispatch = useDispatch();
  const { validSteps } = useSelector((state) => state.nomadInsurance.claims);
  const { isLoading, claimID } = useSelector((state) => state.nomadInsurance.claims);
  const { contractsIncludingExpired } = useSelector(
    (state) => state.nomadInsurance.contract
  );
  const location = useLocation();
  const isChoosePage =
    location.pathname.split('/nomad-insurance/claim').filter(Boolean).length <= 0;
  const isSummary = location.pathname.includes('summary');
  const isConfirmation = location.pathname.includes('confirmation');
  const claimType = location.pathname.includes('non-medical') ? 'non-medical' : 'medical';
  const steps = claimType === 'medical' ? MEDICAL_FLOW : NON_MEDICAL_FLOW;
  const selectedStep = useMemo(
    () => steps.find((step) => location.pathname.includes(step.path)),
    [location.pathname, steps]
  );
  const showBirdImage = isChoosePage || isConfirmation;

  useEffect(() => {
    // Go back to choose page if invalid step
    if (!selectedStep && !isChoosePage && !isConfirmation) {
      history.push('/nomad-insurance/claim');
    }
  });

  useEffect(() => {
    if (claimID) {
      dispatch(fetchClaimByID(claimID));
    }
  }, []);

  useEffect(
    () => () => {
      dispatch(resetState());
    },
    []
  );

  useEffect(() => {
    if (!contractsIncludingExpired) {
      dispatch(fetchContracts());
    } else {
      const hasSafetyWingContract = contractsIncludingExpired.find(
        (x) => x.insuranceProvider === 'SAFETYWING'
      );
      if (!hasSafetyWingContract) {
        history.push('/nomad-insurance/profile/claims');
      }
    }
  }, [dispatch, contractsIncludingExpired]);

  return (
    <Container>
      <LoadingOverlay isLoading={isLoading} variant="light" />
      <InfoCol>
        <HeadingBg>
          <ClaimsBg variant={isChoosePage ? 'small' : 'large'} />
        </HeadingBg>
        <InfoContent>
          <div>
            <FormHeading>Claims form</FormHeading>
            {!isChoosePage && (
              <Stepper
                steps={claimType === 'medical' ? MEDICAL_FLOW : NON_MEDICAL_FLOW}
                validSteps={validSteps}
                selectedStep={selectedStep}
                claimType={claimType}
              />
            )}
          </div>
          {!isChoosePage && (
            <ChatLinkDesktop>
              Have a question?{' '}
              <IntercomLink onClick={() => window.Intercom('showNewMessage')}>
                Chat with us
              </IntercomLink>
            </ChatLinkDesktop>
          )}
        </InfoContent>
      </InfoCol>
      <Content isChoosePage={isChoosePage} isSummary={isSummary}>
        <AnimatePresence exitBeforeEnter>
          <Routes />
        </AnimatePresence>
      </Content>
      {showBirdImage && (
        <Image src={Bird} alt="bird with broken leg" isConfirmation={isConfirmation} />
      )}
      {!isChoosePage && (
        <Footer>
          <img src={WaveBottom} alt="wave background" />
          <ChatLinkMobile>
            Have a question?{' '}
            <IntercomLink onClick={() => window.Intercom('showNewMessage')}>
              Chat with us
            </IntercomLink>
          </ChatLinkMobile>
        </Footer>
      )}
    </Container>
  );
};

export default ClaimsForm;
